// MediaPipe Face Landmarker wrapper.
//
// Gives us 478 3D face landmarks + a ready-made triangle mesh topology.
// Runs in parallel with PoseDetector in the same WASM runtime. We use the
// "lite" variant (~1MB) for speed — 478 landmarks at 30+ fps on CPU.
//
// The triangle list (FACE_MESH_TRIANGLES) is exposed by MediaPipe itself as
// the FaceLandmarker.FACE_LANDMARKS_TESSELATION connector list — ~900
// triangles that cover the entire face surface with proper topology. We
// pre-compute the triangle array on first use from those edges.

import {
  FilesetResolver,
  FaceLandmarker,
  FaceLandmarkerResult,
} from '@mediapipe/tasks-vision';

const WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task';

export interface FaceLandmark {
  x: number; // 0..1 normalized to image width
  y: number; // 0..1 normalized to image height
  z: number; // relative depth
}

export interface FaceDetectionResult {
  /** 478 landmarks in image-normalized coords. */
  landmarks: FaceLandmark[] | null;
}

/**
 * Canonical face mesh triangle list derived from MediaPipe's tesselation
 * edges. We build this once from FaceLandmarker.FACE_LANDMARKS_TESSELATION —
 * a list of {start,end} landmark-index pairs that describe every edge in
 * the canonical face mesh. We walk the edges and group them into triangles.
 *
 * The canonical MediaPipe face mesh has ~468 triangles. We compute it at
 * module init so the render loop can just index into it.
 */
let FACE_MESH_TRIANGLES: [number, number, number][] | null = null;

export function getFaceMeshTriangles(): [number, number, number][] {
  if (FACE_MESH_TRIANGLES) return FACE_MESH_TRIANGLES;

  // FaceLandmarker exposes the tesselation as a list of {start, end} edges.
  // These are the edges of a proper triangulation, so every 3 consecutive
  // edges form a triangle. But we can't rely on ordering — instead we
  // reconstruct triangles by finding every set of 3 mutually-connected
  // vertices.
  //
  // Simpler, correct approach: the tesselation is stable and published in
  // the MediaPipe JS package, so we read it and convert edges → triangles
  // by building an adjacency map.
  //
  // For our purposes we just need a set of triangles that covers the face.
  // We derive them from FaceLandmarker's static tesselation list.

  const edges = FaceLandmarker.FACE_LANDMARKS_TESSELATION as Array<{
    start: number;
    end: number;
  }>;

  // Build adjacency sets
  const adj = new Map<number, Set<number>>();
  for (const { start, end } of edges) {
    if (!adj.has(start)) adj.set(start, new Set());
    if (!adj.has(end)) adj.set(end, new Set());
    adj.get(start)!.add(end);
    adj.get(end)!.add(start);
  }

  // Find all triangles: for each edge (a,b), find vertices c that are
  // adjacent to both a and b. Use a<b<c ordering to dedupe.
  const tris: Record<string, [number, number, number]> = {};
  for (let ei = 0; ei < edges.length; ei++) {
    const { start, end } = edges[ei];
    const a = Math.min(start, end);
    const b = Math.max(start, end);
    const neighborsA = Array.from(adj.get(a)!);
    const neighborsB = adj.get(b)!;
    for (let ni = 0; ni < neighborsA.length; ni++) {
      const c = neighborsA[ni];
      if (c <= b) continue; // ensure a<b<c
      if (neighborsB.has(c)) {
        tris[`${a},${b},${c}`] = [a, b, c];
      }
    }
  }

  FACE_MESH_TRIANGLES = Object.values(tris);
  return FACE_MESH_TRIANGLES;
}

export class FaceDetector {
  private landmarker: FaceLandmarker | null = null;
  private initializing: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.landmarker) return;
    if (this.initializing) return this.initializing;

    this.initializing = (async () => {
      const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
      this.landmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: MODEL_URL,
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numFaces: 1,
        minFaceDetectionConfidence: 0.5,
        minFacePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: false,
      });
    })();

    await this.initializing;
  }

  detect(video: HTMLVideoElement, timestampMs: number): FaceDetectionResult {
    if (!this.landmarker) return { landmarks: null };

    const result: FaceLandmarkerResult = this.landmarker.detectForVideo(
      video,
      timestampMs
    );

    const landmarks: FaceLandmark[] | null =
      result.faceLandmarks && result.faceLandmarks.length > 0
        ? result.faceLandmarks[0].map((lm) => ({
            x: lm.x,
            y: lm.y,
            z: lm.z,
          }))
        : null;

    return { landmarks };
  }

  close(): void {
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
    }
  }
}
